import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
} from "react-table";
import { getAllInteriorDoorEntries } from "../../../components/Forms/api";
import { Button, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../../components/UI/States";

const InteriorDoorFormSummary = () => {
  const [entryData, setEntryData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAllInteriorDoorEntries((d) =>
      setEntryData(Array.isArray(d) ? d : [])
    ).finally(() => setLoading(false));
  }, []);

  const formatPrice = (price) =>
    Number(price).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });

  const columns = useMemo(
    () => [
      { Header: "Customer", accessor: "customer" },
      { Header: "Account #", accessor: "account" },
      { Header: "Job #", accessor: "job" },
      { Header: "Submitted By", accessor: "submitUser" },
      {
        Header: "Total Estimated Price",
        accessor: "totalEstimatedPrice",
        Cell: ({ value }) => (
          <span className="font-semibold tabular-nums text-emerald-600 dark:text-emerald-300">
            {formatPrice(value)}
          </span>
        ),
      },
      {
        Header: "",
        id: "pdfLink",
        disableSortBy: true,
        Cell: ({ row }) => (
          <a
            href={`https://node.bigclumber.com/api/forms/pdf/?id=${row.original._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="app-link inline-flex items-center gap-1"
          >
            View PDF
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </a>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    prepareRow,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: entryData,
      initialState: { pageIndex: 0, pageSize: 15 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter } = state;

  // Display-only roll-up for the KPI row.
  const totalValue = entryData.reduce(
    (sum, entry) => sum + (Number(entry?.totalEstimatedPrice) || 0),
    0
  );

  return (
    <PageShell size="xl">
      <PageHeader
        icon={DoorFrontIcon}
        tone="sky"
        eyebrow="Sales & Leads"
        title="Interior Door Form Summary"
        description="Every submitted interior door form, with its estimated total and PDF."
      />

      {!loading && entryData.length > 0 && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:max-w-md">
          <StatTile
            label="Submitted forms"
            value={entryData.length.toLocaleString()}
          />
          <StatTile
            label="Total estimated"
            value={formatPrice(totalValue)}
            tone="emerald"
          />
        </div>
      )}

      {loading ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading interior door forms…" />
        </div>
      ) : entryData.length === 0 ? (
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={DoorFrontIcon}
            title="No interior door forms submitted yet."
            description="Submitted Interior Door RFQs will be listed here."
          />
        </div>
      ) : (
        <div className="app-card overflow-hidden rounded-2xl border">
          <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <p className="app-muted text-sm tabular-nums">
              {`${entryData.length} submitted form${
                entryData.length === 1 ? "" : "s"
              }`}
            </p>
            <TextField
              size="small"
              placeholder="Search forms…"
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-full sm:w-72"
              inputProps={{ "aria-label": "Search forms" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="app-table" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        className={
                          column.canSort ? "cursor-pointer select-none" : ""
                        }
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.isSorted &&
                            (column.isSortedDesc ? (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ))}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="whitespace-nowrap"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageOptions.length > 1 && (
            <div className="app-divider flex items-center justify-between gap-2 border-t px-4 py-3">
              <span className="app-muted text-sm tabular-nums">
                Page <strong>{state.pageIndex + 1}</strong> of{" "}
                {pageOptions.length}
              </span>
              <div className="flex gap-1">
                <Button
                  size="small"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
};

export default InteriorDoorFormSummary;
